import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchGql } from '@/lib/graphql_client'
import {
  CrmCreateProposalDocument,
  CrmEntitlementsDocument,
  CrmProposalClientContactDocument,
  CrmProposalCountsDocument,
  CrmProposalDetailDocument,
  CrmProposalDocument,
  CrmProposalsDocument,
  CrmReviseProposalDocument,
  type CrmProposalDetailQuery,
  type CrmProposalSort,
  type CrmProposalsQuery,
} from '@/gql/graphql'
import { useContextStore } from '@/lib/session/context-store'

/**
 * Data access for crm-svc proposals (ENT-565 ZDP-T37, R2).
 *
 * Everything is scoped SERVER-side (tenant RLS and the caller's permissions); the FE never filters
 * by tenant. Query keys carry the active tenant so a context switch cannot serve another
 * organisation's cache.
 *
 * Personal data: `crmCreateProposal`'s arguments and `crmProposalClientContact`'s result are the
 * client's name, email and phone. Neither is placed in a query key, a URL, storage or a toast.
 * The contact read is kept out of the cache the moment nothing renders it (`gcTime: 0`).
 */

export type ProposalRow = CrmProposalsQuery['crmProposals']['items'][number]
export type ProposalDetail = CrmProposalDetailQuery['crmProposalDetail']

const root = (tenantId?: string) => ['crm-proposals', tenantId ?? 'self'] as const

export const proposalKeys = {
  root,
  list: (tenantId: string | undefined, query: ProposalListQuery) => [...root(tenantId), 'list', query] as const,
  counts: (tenantId: string | undefined) => [...root(tenantId), 'list', 'counts'] as const,
  detail: (tenantId: string | undefined, id: string) => [...root(tenantId), 'detail', id] as const,
  // Under `detail`, so everything that refreshes the record refreshes its read-only detail too.
  detailRead: (tenantId: string | undefined, id: string) => [...root(tenantId), 'detail', id, 'read'] as const,
  contact: (tenantId: string | undefined, id: string) => [...root(tenantId), 'contact', id] as const,
  entitlements: (tenantId: string | undefined) => [...root(tenantId), 'entitlements'] as const,
}

const useTenantId = () => useContextStore((s) => s.active?.tenantId)

/** The Agency CRM entitlement keys the caller's tenant holds (exempt on the server). */
export function useCrmEntitlements() {
  const tenantId = useTenantId()
  return useQuery({
    queryKey: proposalKeys.entitlements(tenantId),
    queryFn: async () => (await fetchGql(CrmEntitlementsDocument)).crmEntitlements,
    staleTime: 5 * 60_000,
  })
}

export interface ProposalListQuery {
  filter: {
    attention?: boolean
    states?: string[]
    state?: string
    search?: string
    jurisdictionKeys?: string[]
  }
  sort: CrmProposalSort
  /** 1-based. */
  page: number
  pageSize: number
}

/**
 * One numbered page of the listing (offset paging) and the filtered total, both from the server.
 * The previous page stays on screen while the next one loads, so the table does not flash empty.
 */
export function useProposals(query: ProposalListQuery) {
  const tenantId = useTenantId()
  return useQuery({
    queryKey: proposalKeys.list(tenantId, query),
    queryFn: async () => {
      const data = await fetchGql(CrmProposalsDocument, {
        filter: query.filter,
        page: { first: query.pageSize, offset: (query.page - 1) * query.pageSize, sort: query.sort },
      })
      return { items: data.crmProposals.items, total: data.crmProposals.totalCount ?? 0 }
    },
    placeholderData: keepPreviousData,
  })
}

/** The tab counts of the caller's tenant, unfiltered: what the summary cards and tab badges show. */
export function useProposalCounts() {
  const tenantId = useTenantId()
  return useQuery({
    queryKey: proposalKeys.counts(tenantId),
    queryFn: async () => (await fetchGql(CrmProposalCountsDocument)).crmProposalCounts,
  })
}

export function useProposal(id: string) {
  const tenantId = useTenantId()
  return useQuery({
    queryKey: proposalKeys.detail(tenantId, id),
    queryFn: async () => (await fetchGql(CrmProposalDocument, { id })).crmProposal,
    // "Not found" is the server's final answer for this id; retrying it only delays the page.
    retry: false,
  })
}

/**
 * One proposal's read-only detail (`crmProposalDetail`): its inputs by wizard step as display text and
 * its totals as stored decimal strings. Shown as returned; no figure is computed here (FR-67).
 */
export function useProposalDetail(id: string, enabled = true) {
  const tenantId = useTenantId()
  return useQuery({
    queryKey: proposalKeys.detailRead(tenantId, id),
    queryFn: async () => (await fetchGql(CrmProposalDetailDocument, { proposalId: id })).crmProposalDetail,
    enabled,
    retry: false,
  })
}

/**
 * The client's contact for one proposal. AUDITED server-side (one event per actor, proposal and
 * day), so it never runs on its own: the caller turns `enabled` on from an explicit user action.
 */
export function useProposalClientContact(proposalId: string, enabled: boolean) {
  const tenantId = useTenantId()
  return useQuery({
    queryKey: proposalKeys.contact(tenantId, proposalId),
    queryFn: async () => (await fetchGql(CrmProposalClientContactDocument, { proposalId })).crmProposalClientContact,
    enabled,
    retry: false,
    gcTime: 0,
    staleTime: 0,
  })
}

export interface CreateProposalInput {
  dealReference: string
  clientDisplayName: string
  clientEmail: string
  clientMobile: string | null
  /** A uuid the client generates per submit: a retry with the same id returns the first record. */
  requestId: string
}

export function useCreateProposal() {
  const queryClient = useQueryClient()
  const tenantId = useTenantId()
  return useMutation({
    mutationFn: async (input: CreateProposalInput) => (await fetchGql(CrmCreateProposalDocument, input)).crmCreateProposal,
    onSuccess: (proposal) => {
      queryClient.setQueryData(proposalKeys.detail(tenantId, proposal.id), proposal)
      void queryClient.invalidateQueries({ queryKey: [...proposalKeys.root(tenantId), 'list'] })
    },
  })
}

/**
 * Revise a locked proposal (crm-svc ZDP-T38b): opens input round n+1 on the same record, values
 * carried over from the latest version. The caller supplies the `requestId` (one per click, reused
 * on a retry). The answer is the proposal only; the carried-over values are read with
 * `crmProposalWizard`, so everything cached for this proposal from the closed round (wizard view,
 * locked version, document, approver view) is dropped: serving it would show the old round.
 */
export function useReviseProposal() {
  const queryClient = useQueryClient()
  const tenantId = useTenantId()
  return useMutation({
    mutationFn: async (input: { proposalId: string; requestId: string }) => (await fetchGql(CrmReviseProposalDocument, input)).crmReviseProposal,
    onSuccess: (proposal) => {
      const scope = root(tenantId)
      queryClient.removeQueries({
        predicate: ({ queryKey }) => scope.every((part, i) => queryKey[i] === part) && queryKey.includes(proposal.id) && queryKey[scope.length] !== 'detail',
      })
      queryClient.setQueryData(proposalKeys.detail(tenantId, proposal.id), proposal)
      void queryClient.invalidateQueries({ queryKey: [...scope, 'list'] })
      void queryClient.invalidateQueries({ queryKey: [...scope, 'approval', 'queue'] })
    },
  })
}
